const STORAGE_KEY = 'cibli:session-buffer';
const MAX_ENTRIES = 500;

export interface SessionEntry {
  id: string;
  timestamp: number;
  type: 'navigation' | 'interaction' | 'error' | 'analytics' | 'console' | 'network';
  category: string;
  data: Record<string, unknown>;
}

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function readBuffer(): SessionEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed as SessionEntry[];
  } catch {
    return [];
  }
}

function writeBuffer(entries: SessionEntry[]): void {
  const trimmed = entries.length > MAX_ENTRIES ? entries.slice(-MAX_ENTRIES) : entries;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
}

export function initSessionBuffer(): void {
  addEntry({ type: 'analytics', category: 'session_init', data: {} });

  window.addEventListener('error', event => {
    addEntry({
      type: 'error',
      category: 'window.onerror',
      data: {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  window.addEventListener('unhandledrejection', event => {
    const reason = event.reason;
    addEntry({
      type: 'error',
      category: 'unhandledrejection',
      data: {
        message: reason instanceof Error ? reason.message : String(reason),
      },
    });
  });

  const originalConsoleError = console.error;
  console.error = (...args: unknown[]) => {
    addEntry({
      type: 'console',
      category: 'console.error',
      data: { args: args.map(a => (a instanceof Error ? a.message : String(a))) },
    });
    originalConsoleError.apply(console, args);
  };
}

export function addEntry(entry: Omit<SessionEntry, 'id' | 'timestamp'>): void {
  const entries = readBuffer();
  entries.push({ ...entry, id: generateId(), timestamp: Date.now() });
  writeBuffer(entries);
}

export function getEntries(): SessionEntry[] {
  return readBuffer();
}

export function clearEntries(): void {
  localStorage.removeItem(STORAGE_KEY);
}

export function exportAsReport(): string {
  const entries = readBuffer();
  return JSON.stringify(entries);
}
